import { gql } from '@apollo/client';
import {
  FragmentDefinitionNode,
  GraphQLDirective,
  GraphQLEnumType,
  GraphQLEnumValue,
  GraphQLField,
  GraphQLInputField,
  GraphQLNamedType,
  GraphQLOutputType,
  GraphQLSchema,
  SelectionNode,
  isNamedType,
  isTypeDefinitionNode,
  isTypeExtensionNode,
  parse,
  visit,
} from 'graphql';
import { getFieldDef } from 'graphql-language-service';
import _ from 'lodash';

/**
 * TODO: remove getSubjectSlugByGraphQLType & ReferencePatch!  See additional comments below at usage site.
 */
import { getSubjectSlugByGraphQLType } from 'src/app/graph/documentationPage/schemaPage/getSubjectSlugByGraphQLType';
import { ReferencePatch } from 'src/app/graph/hooks/useInternalGraphLinking';

import { assertUnreachable } from '../assertUnreachable';
import { getOrSetRecord } from '../getOrSetRecord';
import {
  SubgraphMetaEntitiesFragment,
  SubgraphMetaSubgraphsFragment,
} from '../graphqlTypes/types';
import { RouteConfig } from '../routeConfig/RouteConfig';

import { hasConnectLink, unwrapNestedType, unwrapType } from './graphqlHelpers';

export const subgraphMetaSubgraphsFragment = gql`
  fragment SubgraphMetaSubgraphsFragment on CompositionResult {
    graphCompositionID
    subgraphConfigs {
      id
      name
      sdl
    }
  }
`;

export const subgraphMetaEntitiesFragment = gql`
  fragment SubgraphMetaEntitiesFragment on EntitiesResponse {
    entities {
      subgraphKeys {
        keys
        subgraphName
      }
      typename
    }
  }
`;

export type SubgraphAndMetadata =
  SubgraphMetaSubgraphsFragment['subgraphConfigs'][number] & {
    isConnector: boolean;
  };
export type SubgraphInputNode =
  | { parentType?: undefined; node: GraphQLNamedType | GraphQLDirective }
  | {
      parentType: GraphQLNamedType;
      node: GraphQLField<unknown, unknown> | GraphQLInputField;
    }
  | { parentType: GraphQLEnumType; node: GraphQLEnumValue };
export type GetSubgraphs = (input: SubgraphInputNode) => SubgraphAndMetadata[];

export type GetIsEntity = (
  node: GraphQLNamedType | GraphQLDirective | GraphQLOutputType,
) => boolean;
type GetIsNameOfEntity = (name: string) => boolean;

export type GetKeyFields = (input: GraphQLNamedType) => {
  keyField: string;
  keyFieldFields: StructuredKey[];
  keyFragment: FragmentDefinitionNode | string;
  fragmentName: string;
  subgraphs: SubgraphAndMetadata[];
}[];

/**
 * TODO:
 * After moving to a shared library, ReferencePatch seems like it doesn't
 * belong in this data structure.  We'll need to refactor to break the
 * dependency back on app routing code and we should be able to generate any
 * links / patches at the app layer rather than inside this data structure.
 * This also applies to getSubjectSlugByGraphQLType, since the slug is an
 * app-layer concern.
 */
type StructuredKey =
  | { value: string; patch: ReferencePatch | null; fields?: StructuredKey[] }
  | { type: string; patch: ReferencePatch | null; fields?: StructuredKey[] };
function sortedJsonString(fields?: StructuredKey[]): string | undefined {
  return fields
    ?.map(
      (o) => `${'type' in o ? o.type : o.value}${sortedJsonString(o.fields)}`,
    )
    .sort()
    .join();
}

interface MemoizedBuildSubgraphMetaProps {
  subgraphConfigs: Parameters<typeof buildSubgraphMetaMap>[0];
  schema?: {
    hash: string;
    createdAt: string | number;
  };
}

export const memoizedBuildSubgraphMetaMap = _.memoize(
  ({ subgraphConfigs }: MemoizedBuildSubgraphMetaProps) =>
    buildSubgraphMetaMap(subgraphConfigs),
  ({ schema }: MemoizedBuildSubgraphMetaProps) =>
    `${schema?.hash}@${schema?.createdAt}`,
);

/**
 * Given a set of subgraph configs from the active schema, returns
 * a map of subgraph name to metadata for subsequent lookups.
 * (The "isConnector" field would be great to port to the registry API btw)
 */
export function buildSubgraphMetaMap(
  subgraphConfigs: SubgraphMetaSubgraphsFragment['subgraphConfigs'] | undefined,
): Record<string, SubgraphAndMetadata> {
  const allSubgraphEntries: Record<string, SubgraphAndMetadata> =
    Object.create(null);
  subgraphConfigs?.forEach((subgraph) => {
    const typeDefs = parse(subgraph.sdl);
    const subgraphEntry = {
      ...subgraph,
      isConnector: hasConnectLink(typeDefs),
    };
    // Keep track of these so we can map back to them later.
    allSubgraphEntries[subgraph.name] = subgraphEntry;
  });
  return allSubgraphEntries;
}

export function buildSubgraphMetaHelpers(
  /**
   * entities resolver is not gated by role, but subgraphConfigs is
   * consumers should be able to see entities
   */
  entities: SubgraphMetaEntitiesFragment['entities'] | undefined,
  subgraphConfigs: SubgraphMetaSubgraphsFragment['subgraphConfigs'] | undefined,
  graphqlSchema: GraphQLSchema | undefined | null,
): {
  showSubgraphs: boolean;
  subgraphMap: Record<string, SubgraphAndMetadata>;
  getSubgraphs: GetSubgraphs;
  getIsEntity: GetIsEntity;
  getIsNameOfEntity: GetIsNameOfEntity;
  getKeyFields: GetKeyFields;
} | null {
  if ((!entities && !subgraphConfigs) || !graphqlSchema) return null;
  const typeSubgraphMap: Record<
    string,
    {
      subgraphs: SubgraphAndMetadata[];
      fields: Record<string, SubgraphAndMetadata[]> & {
        [key in keyof Object]: never;
      };
    }
  > & {
    [key in keyof Object]: never;
  } =
    // use Object.create(null) here because we are using arbitrary strings
    // as keys, and some of those strings might be reserved object keys.
    // Object.create(null) creates an object without the regular prototype functions
    // like toString etc, so we can use those usually reserved keys
    Object.create(null);
  const directiveSubgraphMap: Record<string, SubgraphAndMetadata[]> =
    Object.create(null);

  const typenameToKeysMap = Object.fromEntries(
    entities?.map(({ typename, subgraphKeys }) => [
      typename,
      Object.entries(
        subgraphKeys?.reduce(
          (acc: Record<string, string[]>, { subgraphName, keys }) => {
            keys.forEach((key) => {
              getOrSetRecord(key, [], acc).push(subgraphName);
            });
            return acc;
          },
          {},
        ) ?? {},
      ).map(([key, subgraphs]) => ({ keyField: key, subgraphs })),
    ]) ?? [],
  );

  const allSubgraphEntries: Record<string, SubgraphAndMetadata> =
    Object.create(null);
  subgraphConfigs?.forEach((subgraph) => {
    const typeDefs = parse(subgraph.sdl);
    const subgraphEntry = {
      ...subgraph,
      isConnector: hasConnectLink(typeDefs),
    };
    // Keep track of these so we can map back to them later.
    allSubgraphEntries[subgraph.name] = subgraphEntry;
    visit(typeDefs, {
      enter(node) {
        if ('name' in node && node.name) {
          const typeSubgraphs = getOrSetRecord(
            node.name.value,
            {
              subgraphs: [],
              // use Object.create(null) here because we are using arbitrary strings
              // as keys, and some of those strings might be reserved object keys.
              // Object.create(null) creates an object without the regular prototype functions
              // like toString etc, so we can use those usually reserved keys
              fields: Object.create(null),
            },
            typeSubgraphMap,
          );
          if (isTypeDefinitionNode(node) || isTypeExtensionNode(node)) {
            if (
              !typeSubgraphs.subgraphs.find((s) => s.name === subgraph.name)
            ) {
              typeSubgraphs.subgraphs.push(subgraphEntry);
            }
            const fields =
              'fields' in node
                ? node.fields
                : 'values' in node
                  ? node.values
                  : null;
            fields?.forEach((field) => {
              if (
                !field.directives?.some(
                  (directive) => directive.name.value === 'external',
                )
              ) {
                const fieldSubgraphs = getOrSetRecord(
                  field.name.value,
                  [],
                  typeSubgraphs.fields,
                );
                if (!fieldSubgraphs.find((s) => s.name === subgraph.name)) {
                  fieldSubgraphs.push(subgraphEntry);
                }
              }
            });

            return false;
          } else if (node.kind === 'DirectiveDefinition') {
            const subgraphs = directiveSubgraphMap[node.name.value] ?? [];
            if (!subgraphs.find((s) => s.name === subgraph.name)) {
              subgraphs.push(subgraphEntry);
            }
            directiveSubgraphMap[node.name.value] = subgraphs;
            return false;
          }
        }
      },
    });
  });

  return {
    showSubgraphs: !!subgraphConfigs,
    subgraphMap: allSubgraphEntries,
    getSubgraphs: _.memoize(({ node, parentType }) => {
      if (parentType) {
        const typeLookup = typeSubgraphMap[parentType.name];
        return typeLookup?.fields[node.name] ?? [];
      } else if (node instanceof GraphQLDirective) {
        return directiveSubgraphMap[node.name] ?? [];
      } else {
        return typeSubgraphMap[node.name]?.subgraphs ?? [];
      }
    }),
    getIsEntity(node) {
      if (node instanceof GraphQLDirective) return false;
      const unwrappedNode = unwrapNestedType(node);
      return (
        isNamedType(unwrappedNode) && unwrappedNode.name in typenameToKeysMap
      );
    },
    getIsNameOfEntity(name) {
      return name in typenameToKeysMap;
    },
    getKeyFields: _.memoize((parentType) => {
      const keys = typenameToKeysMap[parentType.name] ?? [];
      const extractNodeKeys = (
        node: SelectionNode,
        parentType2: GraphQLNamedType | null,
      ): StructuredKey[] => {
        switch (node.kind) {
          case 'Field': {
            const fieldInfo =
              (parentType2 &&
                getFieldDef(graphqlSchema, parentType2, node.name.value)) ??
              null;
            return [
              {
                value: node.name.value,
                patch: parentType2 && {
                  linkedGraphqlEntity: node.name.value,
                  category: getSubjectSlugByGraphQLType(parentType2),
                  graphqlTypeName: parentType2.name,
                },
                fields: node.selectionSet?.selections.flatMap((s) =>
                  extractNodeKeys(s, fieldInfo && unwrapType(fieldInfo)),
                ),
              },
            ];
          }
          case 'FragmentSpread':
            return []; // It's not possible to have named fragments in keys
          case 'InlineFragment': {
            const conditionName = node.typeCondition?.name.value;
            if (conditionName && conditionName !== parentType.name) {
              const conditionType =
                graphqlSchema.getTypeMap()[conditionName] ?? null;
              return [
                {
                  type: conditionName,
                  patch: conditionType && {
                    linkedGraphqlEntity: RouteConfig.DELETE,
                    category: getSubjectSlugByGraphQLType(conditionType),
                    graphqlTypeName: conditionType.name,
                  },
                  fields: node.selectionSet?.selections.flatMap((s) =>
                    extractNodeKeys(s, conditionType),
                  ),
                },
              ];
            } else {
              return node.selectionSet.selections.flatMap((s) =>
                extractNodeKeys(s, parentType2),
              );
            }
          }
          default:
            assertUnreachable(node);
        }
      };

      const structuredKeys = Object.values(
        _.groupBy(
          keys.map(({ keyField, subgraphs }) => {
            const fragmentName = `${parentType.name}KeyFragment`;
            const keyFragment = (() => {
              const fragmentString = `fragment ${fragmentName} on ${parentType.name} {\n  ${keyField}\n}`;
              const gqlNoCodegen = gql;
              const fragment = gqlNoCodegen(fragmentString);
              if (fragment.definitions[0]?.kind === 'FragmentDefinition') {
                return fragment.definitions[0];
              } else {
                // Shouldn't happen, default to string value
                return `fragment ${parentType.name}Key on ${parentType.name} { ${keyField} }`;
              }
            })();

            return {
              keyField,
              keyFragment,
              fragmentName,
              subgraphs: subgraphs.map((subgraphName) => {
                const entry = allSubgraphEntries[subgraphName];
                if (!entry) {
                  throw new Error(
                    `expected a subgraph entry for ${subgraphName}!`,
                  );
                }
                return entry;
              }),
              keyFieldFields:
                typeof keyFragment !== 'string'
                  ? keyFragment.selectionSet.selections.flatMap((s) =>
                      extractNodeKeys(s, parentType),
                    )
                  : [],
            };
          }),
          ({ keyFieldFields }) => sortedJsonString(keyFieldFields),
        ),
      ).map((values) =>
        values.reduce((acc, v) => ({
          ...acc,
          subgraphs: _.uniqBy(
            [...acc.subgraphs, ...v.subgraphs],
            (s) => s.name,
          ),
        })),
      );

      return structuredKeys;
    }),
  };
}
